import React, { useState } from 'react';
import {
    Box,
    Button,
    Paper,
    TextField,
    Typography,
    Alert,
    Divider,
} from '@mui/material';
import { useAuth, friendlyAuthError } from '../../auth/AuthContext';
import { PALETTE } from '../../theme';

// Promo writes are restricted server-side (firestore.rules) to this single
// email. Keep this in sync with that rule. The UI gate below mirrors it so the
// admin screen only renders for the real admin — every other visitor (guest or
// a regular signed-in account) sees the sign-in form instead.
const ADMIN_EMAIL = 'tereschenkovv@gmail.com';

const AdminAuthGate = ({ children }) => {
    const { account, loginEmail, loginGoogle, logout } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const currentEmail = account?.email || null;
    const isAdmin = Boolean(currentEmail) && currentEmail.toLowerCase() === ADMIN_EMAIL;

    const run = async (fn) => {
        setSubmitting(true);
        setError(null);
        try {
            await fn();
        } catch (err) {
            setError(friendlyAuthError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSignIn = (e) => {
        e.preventDefault();
        run(() => loginEmail(email, password));
    };

    // Signing out drops back to an anonymous guest session automatically
    // (AuthProvider), so the storefront keeps working in this tab.
    const handleSignOut = () => run(() => logout());

    if (!isAdmin) {
        return (
            <Box sx={{ maxWidth: 420, mx: 'auto', mt: 8, px: 2 }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                        Admin sign-in
                    </Typography>
                    {currentEmail && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Signed in as <strong>{currentEmail}</strong>, which isn’t the admin account.
                        </Alert>
                    )}
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => run(loginGoogle)}
                        disabled={submitting}
                    >
                        Continue with Google
                    </Button>

                    <Divider sx={{ my: 2 }}>or</Divider>

                    <form onSubmit={handleSignIn}>
                        <TextField
                            label="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            margin="normal"
                            autoComplete="email"
                        />
                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            margin="normal"
                            autoComplete="current-password"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{ mt: 2 }}
                            disabled={submitting || !email.trim() || !password}
                        >
                            Sign in
                        </Button>
                    </form>

                    {currentEmail && (
                        <Button size="small" sx={{ mt: 2 }} onClick={handleSignOut} disabled={submitting}>
                            Sign out
                        </Button>
                    )}
                </Paper>
            </Box>
        );
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    backgroundColor: '#f8fafc',
                    borderBottom: '1px solid #e2e8f0',
                }}
            >
                <Typography variant="caption" sx={{ color: PALETTE.textMuted }}>
                    Signed in as <strong>{currentEmail}</strong>
                </Typography>
                <Button size="small" onClick={handleSignOut} disabled={submitting}>
                    Sign out
                </Button>
            </Box>
            {children}
        </Box>
    );
};

export default AdminAuthGate;
